import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { paymentsApi } from '@api/swagger'
import { useAppSnackbar } from '@hooks/useAppSnackbar'
import useLoadingContext from '@hooks/useLoadingContext'
import { useLoadingIds } from '@hooks/useLoadingIds'
import { useMountEffect } from '@hooks/useMountEffect'
import {
  FetchBillingProgramTypeKeyEnum,
  FetchBillingResponse,
  FetchBillingResponseLicensingVariantEnum,
  PaginationResponse,
} from '@swagger'
import { DEFAULT_PAGE_SIZE } from '@utils/constants'
import LoadingProgress from '@components/Elements/LoadingProgress'
import CountryCoordinatorBillingView from './CountryCoordinatorBillingView'
import DefaultBillingView from './DefaultBillingView'
import { BillingFilters } from './interfaces'
import LCDBillingView from './LCDBillingView'

export const defaultBillingFilters = {
  actor: { value: '', id: '' },
  programType: { value: '', id: '' },
  community: { value: '', id: '' },
}

export const defaultBillingPagination = {
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  totalCount: 0,
  orderBy: [],
}

const BillingContainer: React.FC = () => {
  const { actorKey } = useParams()
  const { t } = useTranslation()
  const { handleError } = useAppSnackbar()
  const {
    BillingTab: { fetchBilling: fetchBillingLoadingId },
  } = useLoadingIds()

  const [billingData, setBillingData] = useState<FetchBillingResponse>()
  const [filters, setFilters] = useState<BillingFilters>(defaultBillingFilters)
  const [pagination, setPagination] = useState<
    Pick<PaginationResponse, 'page' | 'pageSize' | 'totalCount' | 'orderBy'>
  >(defaultBillingPagination)

  const defaultFetchBillingErrorMessage = t(
    'BillingLayout.FetchBilling.Error',
    'Error occurred fetching billing.'
  )

  const fetchBilling = async () => {
    try {
      const programTypeId = filters.programType.id

      const programTypeKey = Object.values(
        FetchBillingProgramTypeKeyEnum
      ).includes(programTypeId as FetchBillingProgramTypeKeyEnum)
        ? (programTypeId as FetchBillingProgramTypeKeyEnum)
        : undefined

      const billingResponse = await paymentsApi.fetchBilling({
        actorKey: actorKey ? +actorKey : undefined,
        orderBy: ['begunAt DESC'],
        page: pagination.page,
        pageSize: pagination.pageSize,
        communityKey: !!filters.community.id ? +filters.community.id : 0,
        programTypeKey,
        directorKey: !!filters.actor.id ? +filters.actor.id : 0,
      })

      setBillingData(billingResponse)

      const totalCount =
        billingResponse.licensing?.variant ===
        FetchBillingResponseLicensingVariantEnum.Lcd
          ? billingResponse.licensing?.billingInfo?.pagination?.totalCount || 0
          : billingResponse.licensing?.billing?.pagination?.totalCount || 0

      setPagination((prev) => ({
        ...prev,
        totalCount,
      }))
    } catch (e) {
      await handleError(e, defaultFetchBillingErrorMessage)
    }
  }

  const { triggerFetch } = useLoadingContext({
    asyncFunction: fetchBilling,
    loadingId: fetchBillingLoadingId + JSON.stringify(filters),
  })

  useEffect(() => {
    triggerFetch()
  }, [pagination.page, pagination.pageSize, triggerFetch])

  useEffect(() => {
    // reset the pagination to the initial page (1)
    setPagination(defaultBillingPagination)
    triggerFetch()
  }, [filters, triggerFetch])

  useMountEffect(triggerFetch)

  const handlePagination = (pagination: Partial<PaginationResponse>) => {
    setPagination((prev) => ({
      ...prev,
      ...pagination,
    }))
  }

  let billingViewComponent: React.ReactNode

  if (billingData) {
    switch (billingData.licensing?.variant) {
      case FetchBillingResponseLicensingVariantEnum.Coco:
        billingViewComponent = (
          <CountryCoordinatorBillingView
            billingData={billingData}
            filters={filters}
            handlePagination={handlePagination}
            pagination={pagination}
            setFilters={setFilters}
          />
        )
        break
      case FetchBillingResponseLicensingVariantEnum.Lcd:
        billingViewComponent = (
          <LCDBillingView
            billingData={billingData}
            filters={filters}
            handlePagination={handlePagination}
            pagination={pagination}
            setFilters={setFilters}
          />
        )
        break
      default:
        billingViewComponent = (
          <DefaultBillingView
            billingData={billingData}
            triggerRefetch={triggerFetch}
          />
        )
        break
    }
  }

  if (!billingData) {
    return <LoadingProgress />
  }

  return <>{billingViewComponent}</>
}

export default BillingContainer
